import React from 'react'

import { ampli } from '@Analytics/ampli'
import { Drawer } from '@Components/Drawer'
import { DrawerContentBasic } from '@Components/Drawer/Content'
import { Button } from '@CoreComponents/Buttons/Button'
import { GiftCardMessageInput } from '@Domains/Product/Features/GiftCard/MessageInput'
import { useTranslation } from '@Globals/Translations'

import styles from './GiftCardTextPrompt.module.scss'

/**
 * Prompt drawer for `AddToCartPromptGiftcard` text codes: the add-to-cart call
 * came in without a gift card message, so offer to add one. Submitting with the
 * field left empty adds the gift card without a message (text becomes '').
 * NOTE: `giftcard_text_out_of_bounds` (only reachable by non-UI clients — both
 * inputs cap at the limit) shows the same copy until dedicated wordbooks exist.
 */
export const GiftCardTextDrawer = ({
  loading,
  onSubmit,
  open,
  ...props
}: React.ComponentProps<typeof Drawer> & {
  loading: boolean
  onSubmit: (text: string) => void
}) => {
  const { t } = useTranslation()
  const [giftCardText, setGiftCardText] = React.useState('')
  const [entered, setEntered] = React.useState(false)

  React.useEffect(() => {
    setEntered(true)
    ampli.promptOpen({ source_id: 'giftcard_text_prompt' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Drawer open={open ? entered : open} triggerRender={false} {...props}>
      <DrawerContentBasic
        footer={(
          <Button
            fillWidth
            innerSpacing="medium"
            loading={loading}
            onClick={() => onSubmit(giftCardText)}
            theme="black"
            type="button"
          >
            {t('cms:ADD_TO_CART')}
          </Button>
        )}
        title={t('giftcard:GIFT_CARD_ERROR_NO_MESSAGE_HEADER')}
      >
        <div className={styles.textPrompt}>
          <p>{t('giftcard:GIFT_CARD_ERROR_NO_MESSAGE_CONTENT')}</p>

          <GiftCardMessageInput
            message={giftCardText}
            onchange={setGiftCardText}
            placeholder={t('giftcard:GIFT_CARD_MESSAGE_PLACEHOLDER', 'Your message goes here')}
          />
        </div>
      </DrawerContentBasic>
    </Drawer>
  )
}
